import { Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types"

export type SentStatusKind = "sent" | "failed"

/**
 * Collapse the raw order status into the states the UI can honestly represent.
 *
 * A send today is a one-way email dispatch — there is no vendor-side
 * confirmation, so PENDING_SEND / SENDING / SENT all mean "sent to the vendor"
 * from the user's perspective (no perpetual in-progress spinner). FAILED is the
 * only distinct state. When a vendor dashboard later reports real delivery
 * statuses, extend this mapping rather than the call sites.
 */
export function getSentStatusKind(status: SentScriptMeta["status"]): SentStatusKind {
  return status === "FAILED" ? "failed" : "sent"
}

function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

export interface ScriptSentToVendorCardProps {
  sentMeta: SentScriptMeta
  /** When provided, the whole card becomes a button that opens the order. */
  onOpenOrder?: () => void
  className?: string
}

export function ScriptSentToVendorCard({ sentMeta, onOpenOrder, className }: ScriptSentToVendorCardProps) {
  const { t } = useTranslation()
  const kind = getSentStatusKind(sentMeta.status)
  const failed = kind === "failed"
  const interactive = !!onOpenOrder
  const Root = interactive ? "button" : "div"

  const titlePrefix = t(`opportunities.scripts.sentCard.${kind}.title`)
  const timestamp = formatAbsolute(sentMeta.sentAt)

  return (
    <Root
      {...(interactive
        ? {
            type: "button" as const,
            onClick: onOpenOrder,
            "aria-label": t("opportunities.scripts.sentCard.viewOrderAria"),
          }
        : { role: "status" })}
      className={cn(
        "group/sentcard mt-3 flex w-full items-center gap-3 rounded-lg border border-l-[3px] px-4 py-3.5 text-left shadow-sm transition-shadow",
        failed
          ? "border-destructive/30 border-l-destructive bg-destructive/[0.05]"
          : "border-border border-l-success bg-card",
        interactive &&
          "cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
        className,
      )}
      data-testid="script-sent-to-vendor-card"
      data-status={kind}
    >
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          failed ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success",
        )}
      >
        <Icon name={failed ? "x-circle" : "check-circle"} size="sm" className="text-current" aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <span className="block text-sm leading-snug">
          <span className="text-muted-foreground">{titlePrefix}</span>{" "}
          <span className="font-semibold text-foreground">{sentMeta.vendorName}</span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {!failed && sentMeta.sentByUserName && (
            <>
              {t("opportunities.scripts.sentCard.sentBy")}{" "}
              <span className="text-foreground/80">{sentMeta.sentByUserName}</span>
              <span aria-hidden> · </span>
            </>
          )}
          {failed && (
            <>
              {t("opportunities.scripts.sentCard.attemptedOn")}
              <span aria-hidden> · </span>
            </>
          )}
          <time dateTime={sentMeta.sentAt} className="tabular-nums">
            {timestamp}
          </time>
          {failed && (
            <>
              <span aria-hidden> · </span>
              <span className="text-destructive/80">
                {t("opportunities.scripts.sentCard.failed.note")}
              </span>
            </>
          )}
        </span>
      </div>

      {interactive && (
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 self-center rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors group-hover/sentcard:bg-muted">
          {t("opportunities.scripts.sentCard.viewOrder")}
          <Icon name="chevron-right" size="xs" className="text-current" aria-hidden />
        </span>
      )}
    </Root>
  )
}
